import React, { useState, useEffect } from 'react'
import { apiClient } from '../../lib/apiClient'
import { SubscriptionTier, PLAN_LABELS } from '../../services/subscriptionService'
import styles from './DowngradeComplianceModal.module.css'

interface DowngradeComplianceModalProps {
  orgId: string
  currentTier: SubscriptionTier
  targetTier: SubscriptionTier
  onClose: () => void
  onCompliant: () => void
}

interface ValidationResult {
  is_valid: boolean
  can_proceed: boolean
  usage: {
    total_users: number
    paid_seats: number
    total_projects: number
    total_resources: number
    uses_sso: boolean
  }
  limits: {
    tier: string
    max_users: number
    max_projects: number
    max_resources: number
    included_seats: number
    has_sso: boolean
    has_audit_log: boolean
    has_partner_rates: boolean
    has_revenue_forecast: boolean
    has_gantt: boolean
  }
  warnings: Array<{
    type: string
    message: string
    current?: number
    limit?: number
    feature?: string
    action_required: string
  }>
}

export function DowngradeComplianceModal({
  orgId,
  currentTier,
  targetTier,
  onClose,
  onCompliant,
}: DowngradeComplianceModalProps) {
  const [validation, setValidation] = useState<ValidationResult | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [acknowledged, setAcknowledged] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)

  useEffect(() => {
    loadValidation()
  }, [refreshKey])

  const loadValidation = async () => {
    try {
      setLoading(true)
      const data = await apiClient.post<ValidationResult>(
        `/orgs/${orgId}/subscription/validate-downgrade`,
        { targetTier }
      )
      setValidation(data)
    } catch (err: any) {
      setError(err.message || 'Failed to load validation')
    } finally {
      setLoading(false)
    }
  }

  const handleRefresh = () => {
    setRefreshKey((k) => k + 1)
  }

  const handleProceed = () => {
    if (validation?.can_proceed) {
      onCompliant()
    }
  }

  if (loading) {
    return (
      <div className={styles.overlay}>
        <div className={styles.modal}>
          <div className={styles.content}>
            <p style={{ textAlign: 'center', padding: '40px' }}>
              Checking compliance requirements...
            </p>
          </div>
        </div>
      </div>
    )
  }

  if (!validation) return null

  if(validation.warnings === null) {return null}
  const limitWarnings = validation.warnings.filter(
    (w) => w.type !== 'feature_loss'
  )
  const featureLossWarnings = validation.warnings.filter(
    (w) => w.type === 'feature_loss'
  )


  const canProceed = validation.can_proceed && (featureLossWarnings.length === 0 || acknowledged)

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2 className={styles.title}>
            <span>⚠️</span>
            Downgrade Requirements
          </h2>
          <p className={styles.subtitle}>
            Review and resolve these requirements before downgrading to{' '}
            {PLAN_LABELS[targetTier]}
          </p>
        </div>

        <div className={styles.content}>
          {error && <div className={styles.error}>{error}</div>}

          {validation.can_proceed && limitWarnings.length === 0 && (
            <div className={styles.success}>
              Your organization meets all requirements for the {PLAN_LABELS[targetTier]} plan!
            </div>
          )}

          <div className={styles.usageComparison}>
            <h3 className={styles.sectionTitle}>Current Usage vs. Plan Limits</h3>

            <div className={styles.comparisonRow}>
              <div className={styles.comparisonLabel}>Paid Seats (Admin/Manager)</div>
              <div className={styles.comparisonValues}>
                <span className={styles.currentValue}>
                  {validation.usage.paid_seats}
                </span>
                <span>/</span>
                <span className={styles.limitValue}>
                  {validation.limits.included_seats} included
                </span>
                <span
                  className={`${styles.statusBadge} ${
                    validation.usage.paid_seats <= validation.limits.included_seats
                      ? styles.statusOk
                      : styles.statusExceeded
                  }`}
                >
                  {validation.usage.paid_seats <= validation.limits.included_seats
                    ? 'OK'
                    : 'Exceeded'}
                </span>
              </div>
            </div>

            {validation.limits.max_users > 0 && (
              <div className={styles.comparisonRow}>
                <div className={styles.comparisonLabel}>Total Users</div>
                <div className={styles.comparisonValues}>
                  <span className={styles.currentValue}>
                    {validation.usage.total_users}
                  </span>
                  <span>/</span>
                  <span className={styles.limitValue}>
                    {validation.limits.max_users} max
                  </span>
                  <span
                    className={`${styles.statusBadge} ${
                      validation.usage.total_users <= validation.limits.max_users
                        ? styles.statusOk
                        : styles.statusExceeded
                    }`}
                  >
                    {validation.usage.total_users <= validation.limits.max_users
                      ? 'OK'
                      : 'Exceeded'}
                  </span>
                </div>
              </div>
            )}

            {validation.limits.max_projects > 0 && (
              <div className={styles.comparisonRow}>
                <div className={styles.comparisonLabel}>Projects</div>
                <div className={styles.comparisonValues}>
                  <span className={styles.currentValue}>
                    {validation.usage.total_projects}
                  </span>
                  <span>/</span>
                  <span className={styles.limitValue}>
                    {validation.limits.max_projects} max
                  </span>
                  <span
                    className={`${styles.statusBadge} ${
                      validation.usage.total_projects <= validation.limits.max_projects
                        ? styles.statusOk
                        : styles.statusExceeded
                    }`}
                  >
                    {validation.usage.total_projects <= validation.limits.max_projects
                      ? 'OK'
                      : 'Exceeded'}
                  </span>
                </div>
              </div>
            )}

            {validation.limits.max_resources > 0 && (
              <div className={styles.comparisonRow}>
                <div className={styles.comparisonLabel}>Resources</div>
                <div className={styles.comparisonValues}>
                  <span className={styles.currentValue}>
                    {validation.usage.total_resources}
                  </span>
                  <span>/</span>
                  <span className={styles.limitValue}>
                    {validation.limits.max_resources} max
                  </span>
                  <span
                    className={`${styles.statusBadge} ${
                      validation.usage.total_resources <= validation.limits.max_resources
                        ? styles.statusOk
                        : styles.statusExceeded
                    }`}
                  >
                    {validation.usage.total_resources <= validation.limits.max_resources
                      ? 'OK'
                      : 'Exceeded'}
                  </span>
                </div>
              </div>
            )}
          </div>

          {limitWarnings.length > 0 && (
            <div className={styles.requirementsSection}>
              <h3 className={styles.sectionTitle}>Actions Required</h3>
              <ul className={styles.requirementList}>
                {limitWarnings.map((warning, idx) => (
                  <li key={idx} className={styles.requirementItem}>
                    <div className={styles.requirementHeader}>
                      <span className={styles.requirementIcon}>🔴</span>
                      <div className={styles.requirementText}>
                        <h4 className={styles.requirementTitle}>
                          {warning.type === 'paid_seats_exceeded' &&
                            'Reduce Paid Seats'}
                          {warning.type === 'total_users_exceeded' &&
                            'Remove Users'}
                          {warning.type === 'projects_exceeded' &&
                            'Reduce Projects'}
                          {warning.type === 'resources_exceeded' &&
                            'Remove Resources'}
                        </h4>
                        <p className={styles.requirementDescription}>
                          {warning.message}
                        </p>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {featureLossWarnings.length > 0 && (
            <div className={styles.featureLossSection}>
              <div className={styles.featureLossTitle}>
                <span>⚠️</span>
                Features Not Available in {PLAN_LABELS[targetTier]} Plan
              </div>
              <ul className={styles.featureList}>
                {featureLossWarnings.map((warning, idx) => (
                  <li key={idx}>{warning.feature}</li>
                ))}
              </ul>
            </div>
          )}

          {featureLossWarnings.length > 0 && validation.can_proceed && (
            <div className={styles.acknowledgment}>
              <div className={styles.checkbox}>
                <input
                  type="checkbox"
                  id="acknowledge-features"
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                />
                <label htmlFor="acknowledge-features">
                  I understand I will lose access to the features listed above when
                  downgrading to the {PLAN_LABELS[targetTier]} plan
                </label>
              </div>
            </div>
          )}
        </div>

        <div className={styles.actions}>
          <button
            className={`${styles.button} ${styles.buttonSecondary}`}
            onClick={onClose}
          >
            Cancel
          </button>
          <div style={{ flex: 1 }} />
          {!validation.can_proceed && (
            <button
              className={`${styles.button} ${styles.buttonSecondary}`}
              onClick={handleRefresh}
            >
              Refresh Status
            </button>
          )}
          <button
            className={`${styles.button} ${styles.buttonPrimary}`}
            onClick={handleProceed}
            disabled={!canProceed}
          >
            {validation.can_proceed
              ? 'Proceed with Downgrade'
              : 'Resolve Issues to Continue'}
          </button>
        </div>
      </div>
    </div>
  )
}
